import { DateString, SignatureOrOffline, ValidationStatus } from './common';
import { Vehicle, WagonDamageReportsLabels } from './Vehicle';
import { BrakingBulletin } from './BrakingBulletin';
import { Traceability } from './Traceability';
import { ReadyForDispatchSignature } from './ReadyForDispatch';

export type CompositionType = 'TRAIN';

export type TrainProblemStatus = 'STALLED' | 'ACCIDENT' | 'AGT_NON_AVAILABILITY' | 'DIVERSE' | 'OK' | 'CANCELLED';

export const TRAIN_PROBLEM_STATUSES: Record<TrainProblemStatus, string> = {
  STALLED: 'Calé',
  ACCIDENT: 'Accident',
  AGT_NON_AVAILABILITY: 'AGT',
  DIVERSE: 'Problème inconnu',
  OK: 'OK',
  CANCELLED: 'Annulé',
};

export type TrainSummary = Readonly<{
  id: string;
  originalMissionId?: string;
  originalTrainNumber?: string;
  originalTrainStartDate?: DateString;
  steps: TrainStepSummary[];
}>;

export type TrainStepSummary = Readonly<{
  id: string;
  type: CompositionType;
  trainNumber: string;
  startDate: DateString;
  endDate: DateString;
  startLocationLabel: string;
  startStationId: string;
  endLocationLabel: string;
  endStationId: string;
  marketName: string;
  status: ValidationStatus;
  problemStatus: TrainProblemStatus;
  hasSignedBrakingBulletin: boolean;
  hasSignedTraceability: boolean;
  hasSignedReadyForDispatch: boolean;
  manuallyUpdated: boolean;
  manualBrakingBulletin: boolean;
  manualTraceability: boolean;
  manualReadyForDispatch: boolean;
  compositionAutoGenerated: boolean;
  version: number;
}>;

export type Train = Readonly<{
  id: string;
  originalMissionId?: string;
  originalTrainNumber?: string;
  originalTrainStartDate?: DateString;
  originalLastVisibleCompositionId?: string;
  steps: TrainStep[];
}>;

export type CachedTrain = Readonly<{
  id: string;
  originalMissionId?: string;
  originalTrainNumber?: string;
  originalTrainStartDate?: DateString;
  originalLastVisibleCompositionId?: string;
  /**
   * CompositionIds of the steps (Not the stepId field)
   */
  stepIds: string[];
}>;

export type TrainStepAgent = Readonly<{
  id: string;
  name: string;
  phone: string;
  typeCode: string;
  typeLabel: string;
}>;

export type TrainStep = Readonly<{
  id: string;
  trainId: string;
  type: CompositionType;
  trainNumber: string;
  ateNumber: string | null;
  startDate: DateString;
  endDate: DateString;
  startLocationLabel: string;
  startStationId: string;
  endLocationLabel: string;
  endStationId: string;
  marketId: number;
  marketName: string;
  status: ValidationStatus;
  stepProblemStatus: TrainProblemStatus;
  version: number;
  validatedDate: DateString | null;
  validatedBy: string | null;
  updateDate: DateString;
  updatedBy: string;
  agentNames: readonly string[];
  agents: readonly TrainStepAgent[];
  combinedMarket: boolean;
  indiceComposition: string | null;
  catStat: string | null;
  manuallyUpdated: boolean;
  manualBrakingBulletin: boolean;
  manualTraceability: boolean;
  manualReadyForDispatch: boolean;
  compositionAutoGenerated: boolean;
  vehicles: readonly Vehicle[];
  wagonsDamageReportsLabels: readonly WagonDamageReportsLabels[];
  brakingBulletins: readonly BrakingBulletin[];
  traceability?: Traceability;
  readyForDispatchSignatures: readonly SignatureOrOffline<ReadyForDispatchSignature>[];
  missionEndSignatures: readonly SignatureOrOffline[];
}>;

export const compositionHasStepProblems = (composition: TrainStepSummary) => composition.problemStatus !== 'OK';
